/**
 * AccessibilityStatement — linked from the floating accessibility widget's
 * footer (AccessibilityWidget.tsx) on every page. English-only, like the rest
 * of the site (QA T8.4); references Israeli Standard 5568 / WCAG 2.1 AA as the
 * conformance target. Rendered in the shared Legal Center layout like /terms
 * and /privacy.
 */
import LegalPageLayout, { TocSection } from './LegalPageLayout';

const TOC_SECTIONS: TocSection[] = [
  { id: 'commitment', title: 'Our Commitment' },
  { id: 'features', title: 'Accessibility Features' },
  { id: 'level', title: 'Conformance Level' },
  { id: 'limitations', title: 'Known Limitations' },
  { id: 'contact-a11y', title: 'Accessibility Contact' },
];

export default function AccessibilityStatement() {
  return (
    <LegalPageLayout
      title="Accessibility Statement"
      subtitle="Accessibility Statement — BuzzBit X"
      effectiveDate="July 26, 2026"
      lastUpdated="August 2, 2026"
      sections={TOC_SECTIONS}
    >
      {/* Our Commitment */}
      <section id="commitment" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">Our Commitment</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          BuzzBit X is committed to providing equal service to all users, including people
          with disabilities. We work continuously to improve the accessibility of our website
          and product so that everyone can use our services easily and comfortably.
        </p>
      </section>

      {/* Accessibility Features */}
      <section id="features" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">Accessibility Features</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          A dedicated accessibility widget (opened from the floating accessibility button)
          is available on every page and offers, among other adjustments:
        </p>
        <ul className="list-disc ml-6 space-y-2 text-gray-600 mb-6">
          <li>Increasing and decreasing text size</li>
          <li>High-contrast mode</li>
          <li>Link highlighting</li>
          <li>A dyslexia-friendly font</li>
          <li>Stopping animations and motion</li>
          <li>A reading guide</li>
          <li>Hiding images</li>
          <li>Enhanced keyboard-focus outlines and keyboard navigation</li>
          <li>Resetting all settings with one click</li>
        </ul>
        <p className="text-gray-600 leading-relaxed">
          Your preferences are saved in the browser and apply across all pages.
        </p>
      </section>

      {/* Conformance Level */}
      <section id="level" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">Conformance Level</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          We work toward conformance with the accepted accessibility guidelines — Israeli
          Standard 5568, which is based on WCAG 2.1 Level AA — and keep improving conformance
          continuously as part of product development.
        </p>
      </section>

      {/* Known Limitations */}
      <section id="limitations" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">Known Limitations</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          Despite our efforts, some parts of the product — including user-generated content,
          interactive charts, and third-party components — may not yet be fully accessible.
          We continue to work on improving them. If you encounter a component that is not
          accessible, please let us know and we will address it promptly.
        </p>
      </section>

      {/* Accessibility Contact */}
      <section id="contact-a11y" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">Accessibility Contact</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          Found an accessibility issue? Have a suggestion? Contact us at{' '}
          <a href="mailto:legal@buzzbitx.com" className="text-teal-500 underline hover:text-teal-400">
            legal@buzzbitx.com
          </a>
          . Please include the page and the issue, and we will do our best to respond and
          resolve it as quickly as possible.
        </p>
      </section>
    </LegalPageLayout>
  );
}
